<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import PaywallService from '@/services/PaywallService'
import FormButton from '../Atoms/FormButton.vue'
import FormCheckbox from '../Atoms/FormCheckbox.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
  currentPaywall: {
    type: Object,
    required: false,
  },
})

const store = useStore()
const { notify } = useNotification()

const paywalls = computed(() => store.getters['Paywalls/paywalls'] || [])
const otherPaywalls = computed(() =>
  paywalls.value.filter((p) => Number(p.id) !== Number(props.currentPaywall?.id))
)

const selected = ref([]) // array of paywall (stream) ids this paywall accepts access from
const mutual = ref(false)
const saving = ref(false)

const nameOf = (p) => p.internalName || p.title || `Paywall ${p.id}`

const syncSelection = () => {
  const from = Array.isArray(props.currentPaywall?.access_from)
    ? props.currentPaywall.access_from.map(Number)
    : []
  selected.value = [...from]
  mutual.value = false
}
onMounted(syncSelection)
watch(() => props.currentPaywall?.id, syncSelection)

const isChecked = (id) => selected.value.includes(Number(id))
const toggle = (id) => {
  const n = Number(id)
  selected.value = selected.value.includes(n)
    ? selected.value.filter((x) => x !== n)
    : [...selected.value, n]
}

const dirty = computed(() => {
  const cur = new Set((props.currentPaywall?.access_from || []).map(Number))
  if (cur.size !== selected.value.length) return true
  return selected.value.some((x) => !cur.has(x))
})

const save = async () => {
  if (saving.value) return
  const id = props.currentPaywall.id
  saving.value = true
  try {
    await PaywallService.updatePaywallAccessFrom(id, selected.value)
    store.dispatch('Paywalls/setPaywallProp', { id, prop: 'access_from', value: [...selected.value] })

    // Mutual: also add this paywall to each selected paywall's list (reverse link).
    if (mutual.value && selected.value.length) {
      for (const otherId of selected.value) {
        const other = paywalls.value.find((p) => Number(p.id) === Number(otherId))
        const existing = Array.isArray(other?.access_from) ? other.access_from.map(Number) : []
        if (existing.includes(Number(id))) continue
        const next = [...existing, Number(id)]
        await PaywallService.updatePaywallAccessFrom(otherId, next)
        store.dispatch('Paywalls/setPaywallProp', { id: otherId, prop: 'access_from', value: next })
      }
    }

    track(EVENTS.PAYWALL_ACCESS_GRANTED, {
      paywallId: id,
      grantedCount: selected.value.length,
      isMutual: mutual.value,
    })

    notify({ group: 'success', text: 'Shared access updated' })
  } catch (err) {
    notify({ group: 'error', text: err.message || "Couldn't update shared access" })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
    <div class="mb-3">
      <h4 class="text-sm font-semibold text-white mb-0.5">Shared access</h4>
      <p class="text-tiny text-surface-7 mb-0">Grant access to viewers who already have active access to another
        paywall — useful for bundles or upsells.</p>
    </div>

    <div v-if="!otherPaywalls.length" class="rounded-lg bg-surface-2 p-4 text-center">
      <p class="text-tiny text-surface-7 mb-0">Create another paywall to share access with it.</p>
    </div>

    <template v-else>
      <p class="text-tiny text-surface-8 mb-2">Also allow viewers who have access to:</p>
      <div class="flex flex-col divide-y divide-surface-3 max-h-64 overflow-y-auto rounded-lg bg-surface-2 ring-1 ring-white/[0.04]">
        <label v-for="p in otherPaywalls" :key="p.id"
          class="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-surface-3 transition-colors">
          <FormCheckbox class="shrink-0" :modelValue="isChecked(p.id)" @change="toggle(p.id)" />
          <span class="text-sm text-surface-text-1 truncate">{{ nameOf(p) }}</span>
        </label>
      </div>

      <label class="flex items-center gap-2 mt-3 cursor-pointer">
        <FormSwitch size="sm" v-model="mutual" />
        <span class="text-tiny text-surface-7">Make mutual — this paywall's viewers also get access to the selected
          paywalls</span>
      </label>

      <div class="flex justify-end mt-3">
        <FormButton size="sm" :disabled="saving || (!dirty && !mutual)"
          :label="saving ? 'Saving…' : 'Save shared access'" @click="save" />
      </div>
    </template>
  </section>
</template>

<style scoped></style>
